import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { convertDisplayToDb, formatCurrency, getCurrencySymbol } from "@/utils/currency";

interface TaxCalculationSectionProps {
  baseFee: string;
  onBaseFeeChange: (value: string) => void;
  gstApplied: boolean;
  onGstAppliedChange: (checked: boolean) => void;
  gstPercentage: string;
  onGstPercentageChange: (value: string) => void;
  calculatedGstAmount: number;
  tdsDeducted: boolean;
  onTdsDeductedChange: (checked: boolean) => void;
  tdsPercentage: string;
  onTdsPercentageChange: (value: string) => void;
  calculatedTdsAmount: number;
  netPayable: number;
  userCountry?: string | null;
}

export const TaxCalculationSection: React.FC<TaxCalculationSectionProps> = ({
  baseFee,
  onBaseFeeChange,
  gstApplied,
  onGstAppliedChange,
  gstPercentage,
  onGstPercentageChange,
  calculatedGstAmount,
  tdsDeducted,
  onTdsDeductedChange,
  tdsPercentage,
  onTdsPercentageChange,
  calculatedTdsAmount,
  netPayable,
  userCountry,
}) => {
  return (
    <>
      <div className="md:col-span-2">
        <Label htmlFor="baseFee">
          Base Fee Amount ({getCurrencySymbol(userCountry)})
        </Label>
        <Input
          id="baseFee"
          type="number"
          placeholder="0"
          value={baseFee}
          onChange={(e) => onBaseFeeChange(e.target.value)}
          required
        />
        <p className="text-sm text-gray-600 mt-1">
          Enter the base fee amount (excluding GST and TDS)
        </p>
      </div>

      {/* GST Section */}
      <div className="md:col-span-2 space-y-3 p-4 border rounded-lg bg-gray-50">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="gst_applied"
            checked={gstApplied}
            onCheckedChange={(checked) => onGstAppliedChange(!!checked)}
          />
          <Label htmlFor="gst_applied">Apply GST?</Label>
        </div>

        {gstApplied && (
          <div>
            <Label htmlFor="gstPercentage">GST Percentage (%)</Label>
            <Input
              id="gstPercentage"
              type="number"
              min={0}
              step="0.1"
              placeholder="18.0"
              value={gstPercentage}
              onChange={(e) => onGstPercentageChange(e.target.value)}
            />
            <p className="text-sm text-gray-600 mt-1">
              GST will be calculated as a percentage of base fee.
            </p>
          </div>
        )}
      </div>

      {/* TDS Section */}
      <div className="md:col-span-2 space-y-3 p-4 border rounded-lg bg-orange-50">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="tds_deducted"
            checked={tdsDeducted}
            onCheckedChange={(checked) => onTdsDeductedChange(!!checked)}
          />
          <Label htmlFor="tds_deducted">Deduct TDS?</Label>
        </div>

        {tdsDeducted && (
          <div>
            <Label htmlFor="tdsPercentage">TDS Percentage (%)</Label>
            <Input
              id="tdsPercentage"
              type="number"
              min={0}
              step="0.1"
              placeholder="10.0"
              value={tdsPercentage}
              onChange={(e) => onTdsPercentageChange(e.target.value)}
            />
            <p className="text-sm text-gray-600 mt-1">
              TDS will be calculated as a percentage of base fee.
            </p>
          </div>
        )}
      </div>

      {/* Payment Breakdown */}
      {baseFee && (
        <div className="md:col-span-2 space-y-2 p-4 border rounded-lg bg-blue-50">
          <h4 className="font-semibold text-sm">Payment Breakdown</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Base Fee:</span>
              <span>
                {formatCurrency(
                  convertDisplayToDb(parseFloat(baseFee) || 0, userCountry),
                  userCountry
                )}
              </span>
            </div>
            {gstApplied && (
              <div className="flex justify-between text-green-700">
                <span>+ GST ({gstPercentage}%):</span>
                <span>
                  {formatCurrency(
                    convertDisplayToDb(calculatedGstAmount, userCountry),
                    userCountry
                  )}
                </span>
              </div>
            )}
            {tdsDeducted && (
              <div className="flex justify-between text-red-700">
                <span>- TDS ({tdsPercentage}%):</span>
                <span>
                  {formatCurrency(
                    convertDisplayToDb(calculatedTdsAmount, userCountry),
                    userCountry
                  )}
                </span>
              </div>
            )}
            <div className="flex justify-between font-semibold border-t pt-2">
              <span>Net Payable Amount:</span>
              <span>
                {formatCurrency(
                  convertDisplayToDb(netPayable, userCountry),
                  userCountry
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
